/**
 * Email validation regex pattern
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates email address
 * @param {string} email - Email to validate
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export const validateEmail = (email) => {
    if (!email) {
        return { valid: false, error: 'Email is required' };
    }
    if (!EMAIL_REGEX.test(email.trim())) {
        return { valid: false, error: 'Invalid email format' };
    }
    return { valid: true };
};

/**
 * Validates phone number (must be exactly 10 digits)
 * @param {string} phone - Phone number to validate
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export const validatePhone = (phone) => {
    if (!phone) {
        return { valid: true }; // Optional
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
        return { valid: false, error: 'Phone number must be exactly 10 digits' };
    }
    return { valid: true };
};

/**
 * Validates birthday (month and day)
 * @param {string} monthStr - Month string (01-12)
 * @param {string} dayStr - Day string (01-31)
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidBirthday = (monthStr, dayStr) => {
    if (!monthStr || !dayStr) {
        return true; // Optional
    }
    const m = parseInt(monthStr, 10);
    const d = parseInt(dayStr, 10);
    if (!Number.isFinite(m) || !Number.isFinite(d)) {
        return false;
    }
    if (m < 1 || m > 12 || d < 1 || d > 31) {
        return false;
    }

    const year = 2000;
    const mm = String(m).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    const date = new Date(`${year}-${mm}-${dd}T00:00:00Z`);

    return (
        date.getUTCFullYear() === year &&
        date.getUTCMonth() + 1 === m &&
        date.getUTCDate() === d
    );
};

/**
 * Validates password
 * @param {string} password - Password to validate
 * @param {number} minLength - Minimum password length (default: 8; align with Supabase auth settings)
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export const validatePassword = (password, minLength = 8) => {
    if (!password) {
        return { valid: false, error: 'Password is required' };
    }
    if (password.length < minLength) {
        return {
            valid: false,
            error: `Password must be at least ${minLength} characters`,
        };
    }
    return { valid: true };
};

/**
 * Validates name (must be at least 2 characters)
 * @param {string} name - Name to validate
 * @returns {{valid: boolean, error?: string}} Validation result
 */
export const validateName = (name) => {
    if (!name || !name.trim()) {
        return { valid: false, error: 'Name is required' };
    }
    if (name.trim().length < 2) {
        return { valid: false, error: 'Name must be at least 2 characters' };
    }
    return { valid: true };
};

